// =============================================================================
// ADD DROPDOWN LIST
// Adds an Excel data validation dropdown to a range of cells in a worksheet.
// Uses a hidden "Lists" sheet to store the dropdown values, which allows
// dropdowns with more than 255 characters total in the list.
//
// Compatible with ExcelJS ^4.4.0
//
// @param workbook   - ExcelJS workbook instance
// @param worksheet  - ExcelJS worksheet to apply the dropdown to
// @param config
//   @param config.column    - column letter e.g. "A", "B", "C"
//   @param config.startRow  - first row to apply dropdown (usually 2, after header)
//   @param config.endRow    - last row to apply dropdown (usually 1000)
//   @param config.listName  - name for this list, used as header in Lists sheet
//   @param config.data      - string[] of values for the dropdown
// =============================================================================
export const addDropdownList = async (workbook, worksheet, {
  column,
  startRow,
  endRow,
  listName,
  data,
}) => {
  // Find or create the hidden Lists sheet
  let listsSheet = workbook.getWorksheet("Lists");
  if (!listsSheet) {
    listsSheet = workbook.addWorksheet("Lists");
    listsSheet.state = "hidden";
    // Must use addRow() before any getCell() calls on a new sheet
    listsSheet.addRow(["__hidden_validation_list__"]);
  }

  // Scan row 1 to find the first empty column
  // Start at column 2 (B) — column 1 (A) is reserved for the marker
  let colIndex = 2;
  while (listsSheet.getRow(1).getCell(colIndex).value) {
    colIndex++;
  }

  // Write the list name as header in row 1
  listsSheet.getRow(1).getCell(colIndex).value = listName;

  // Write each dropdown value starting from row 2
  data.forEach((val, idx) => {
    listsSheet.getRow(idx + 2).getCell(colIndex).value = val;
  });

  // Build the named range reference e.g. Lists!$B$2:$B$84
  const lastListRow = data.length + 1;
  const colLetter = listsSheet.getColumn(colIndex).letter;
  const listRange = `Lists!$${colLetter}$2:$${colLetter}$${lastListRow}`;

  // ExcelJS 4.4.0 compatible approach:
  // Ensure rows exist by committing them first, then apply
  // dataValidation directly on the cell object per row.
  // Using getRow().commit() initializes the row without needing values.
  for (let i = startRow; i <= endRow; i++) {
    const row = worksheet.getRow(i);
    // Only commit if row has not been initialized yet
    if (!row.hasValues) {
      row.commit();
    }
    row.getCell(column).dataValidation = {
      type: "list",
      allowBlank: true,
      formulae: [`=${listRange}`],
      showErrorMessage: true,
      errorStyle: "warning",
      errorTitle: `Invalid ${listName}`,
      error: `Please select a valid ${listName} from the dropdown.`,
      showInputMessage: true,
      promptTitle: `Select ${listName}`,
      prompt: `Choose a ${listName} from the dropdown.`,
    };
  }
};